import { CSSProperties, useMemo } from "react";
import { useAppContext } from "../../../../AppContext";
import { PaymentTypes, UserEventStatus } from "../../../../common/enums";
import { IconType } from "../../../../common/icons/Icon";
import { brightenForDarkBackground } from "../../../../common/infrastructure/utilities/colorUtility";
import { isReservationCancelled } from "../../../../common/infrastructure/utilities/reservationUtility";
import { colorToRgba } from "../../../../common/components/common/Tag/utils/tagColorUtils";
import {
  humanizePaymentType,
  studioAcceptsMultisport,
} from "../../../../common/utilities/paymentUtility";
import { IRegisteredUserViewModel } from "../models/calendarEventModel";
import {
  getEventRegistrationStatusText,
  getReservationTone,
  ReservationTone,
} from "../utils/portalCalendarUtility";

export interface IEventDetailsContentProps {
  eventName?: string;
  reservationId?: number;
  instructor?: string;
  dateLabel?: string;
  timeLabel?: string;
  studioName?: string;
  address?: string;
  mapsLink?: string;
  phoneNumber?: string;
  phoneNumberDescription?: string;
  secondaryPhoneNumber?: string;
  secondaryPhoneNumberDescription?: string;
  description?: string;
  paymentType?: PaymentTypes;
  paymentPlanId?: number;
  paymentPlanName?: string | null;
  reservations?: IRegisteredUserViewModel[];
  pricePaid?: number;
  /** Only legacy (pre-euro) reservations carry BGN here; anything else is EUR. */
  currencyId?: number;
  numberOfUserVisit?: number;
  totalPlanVisits?: number;
  isCancelled?: boolean;
  /** The class itself will not take place (not the viewer's reservation being cancelled). */
  isEventCancelled?: boolean;
  warningMessage?: string;
  hallName?: string;
  price?: number;
  isPassed?: boolean;
  status?: UserEventStatus;
  /** Whether the class takes MultiSport; left out, the dialog says nothing about it. */
  isMultisportAvailable?: boolean;
  /** A remote (Google Meet) appointment. */
  isRemote?: boolean;
  /** The Meet link; the server fills it only for the client's own reservation. */
  meetingUrl?: string | null;
}

export interface IReservationStatusRow {
  key: number;
  text: string;
  tone: ReservationTone;
  icon: IconType;
}

const TONE_ICONS: Record<ReservationTone, IconType> = {
  booked: IconType.Valid,
  waiting: IconType.Wait,
  cancelled: IconType.Cancel,
};

const hasContent = (html?: string) => !!html?.replace(/<[^>]*>/g, "").trim();

export const useEventDetailsContent = ({
  reservationId,
  instructor,
  studioName,
  address,
  mapsLink,
  phoneNumber,
  secondaryPhoneNumber,
  description,
  paymentType,
  paymentPlanId,
  paymentPlanName,
  reservations,
  pricePaid,
  numberOfUserVisit,
  totalPlanVisits,
  isCancelled,
  isEventCancelled,
  warningMessage,
  hallName,
  isPassed,
  status,
  isMultisportAvailable,
  isRemote,
  meetingUrl,
}: IEventDetailsContentProps) => {
  const { selectedStudio, currentUser } = useAppContext();

  // The studio's brand colour, brightened for the dark glass, tints the icons.
  const accentStyle = useMemo(() => {
    const accent = selectedStudio?.backgroundColorSecondary
      ? brightenForDarkBackground(selectedStudio.backgroundColorSecondary)
      : "#ffffff";

    return {
      "--details-accent": accent,
      "--details-accent-soft": colorToRgba(accent, 0.16),
    } as CSSProperties;
  }, [selectedStudio]);

  const isEffectivelyCancelled =
    isCancelled !== undefined &&
    status !== undefined &&
    isReservationCancelled(isCancelled, status);

  const showMultisport =
    isMultisportAvailable !== undefined &&
    studioAcceptsMultisport(selectedStudio?.supportedPaymentTypes);

  // A reservation that came without a known status has no text, so it is left out.
  const reservationRows: IReservationStatusRow[] = (reservations ?? [])
    .map((x) => ({
      key: x.userId,
      text: getEventRegistrationStatusText(
        x.status,
        currentUser?.id === x.userId,
        x.isCancelled,
        x.type,
        x.userName,
        isPassed,
      ),
      tone: getReservationTone(x.status, x.isCancelled),
    }))
    .filter((x) => !!x.text)
    .map((x) => ({ ...x, icon: TONE_ICONS[x.tone] }));

  const showPaymentType = !isEffectivelyCancelled && !!paymentType;
  const showPaidAmount =
    !isEffectivelyCancelled &&
    !!pricePaid &&
    paymentType !== PaymentTypes.Multisport &&
    paymentType !== PaymentTypes.CoolFit;
  const showVisits = !isEffectivelyCancelled && !!numberOfUserVisit;
  const showReservationMeta =
    !!reservationId || showPaymentType || showPaidAmount || showVisits;

  const studioNameLabel = hallName ? `${studioName}, ${hallName}` : studioName;
  const showStudioName = !!studioNameLabel;
  const showAddress = !!address && !!mapsLink;
  const showPhone = !!phoneNumber;
  const showSecondaryPhone = !!secondaryPhoneNumber;

  // A cancelled reservation, a cancelled class or a finished class has no meeting to join.
  const hasMeetingLink = !!meetingUrl?.trim();
  const showMeeting =
    (!!isRemote || hasMeetingLink) &&
    !isEffectivelyCancelled &&
    !isEventCancelled &&
    !isPassed;

  return {
    accentStyle,
    showMultisport,
    isMultisportAccepted: !!isMultisportAvailable,
    showInstructor: !!instructor?.trim(),
    showWarning: !!warningMessage && !isEffectivelyCancelled,
    reservationRows,
    showReservationSection: reservationRows.length > 0 || !!reservationId,
    reservationSectionTitle:
      reservationRows.length > 1 ? "Резервации" : "Резервация",
    showReservationMeta,
    showPaymentType,
    paymentTypeLabel: humanizePaymentType(
      paymentType,
      paymentPlanId,
      paymentPlanName,
    ),
    showPaidAmount,
    showVisits,
    visitsUsedLabel:
      !!totalPlanVisits && !!numberOfUserVisit
        ? `${numberOfUserVisit}/${totalPlanVisits}`
        : "N/A",
    studioNameLabel,
    showStudioName,
    showAddress,
    showPhone,
    showSecondaryPhone,
    showPlaceSection:
      showStudioName || showAddress || showPhone || showSecondaryPhone,
    showDescription: hasContent(description),
    showMeeting,
    hasMeetingLink,
  };
};
